// Use API routes as proxy to falcon-mailer
const API_BASE_URL = '/api/mailbox';

export interface Conversation {
  id: string;
  user_id: string;
  subject: string;
  participants: string[];
  last_message_at: string;
  message_count: number;
  is_read: boolean;
  is_campaign: boolean;
  is_non_replyable?: boolean;
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: string;
  conversation_id: string;
  message_id: string;
  from: string;
  to: string[];
  subject: string;
  body: string;
  html_body?: string;
  is_outgoing: boolean;
  is_read: boolean;
  sent_at: string;
  created_at: string;
  updated_at: string;
}

export interface GetConversationsResponse {
  conversations: Conversation[];
  total: number;
}

export interface GetMessagesResponse {
  messages: Message[];
  total: number;
}

export interface SendReplyRequest {
  from: string;
  to: string[];
  subject: string;
  body: string;
  html_body?: string;
}

export interface SendReplyResponse {
  message_id: string;
  status: string;
  error?: string;
}

export interface SearchResponse {
  conversations: Conversation[];
  messages: Message[];
  total: number;
}

export class MailboxService {
  private async makeRequest(endpoint: string, options: RequestInit = {}) {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  }

  async getConversations(_userID: string, search?: string, limit = 50, offset = 0, isCampaign?: boolean): Promise<GetConversationsResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });
    
    if (search) {
      params.append('search', search);
    }

    if (isCampaign !== undefined) {
      params.append('isCampaign', isCampaign.toString());
    }

    return this.makeRequest(`/conversations?${params}`);
  }

  async getConversation(_userID: string, conversationID: string): Promise<Conversation> {
    return this.makeRequest(`/conversations/${conversationID}`);
  }

  async getMessages(_userID: string, conversationID: string, limit = 50, offset = 0): Promise<GetMessagesResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: offset.toString(),
    });

    return this.makeRequest(`/conversations/${conversationID}/messages?${params}`);
  }

  async sendReply(conversationID: string, reply: SendReplyRequest): Promise<SendReplyResponse> {
    return this.makeRequest(`/conversations/${conversationID}/reply`, {
      method: 'POST',
      body: JSON.stringify(reply),
    });
  }

  async markConversationAsRead(userID: string, conversationID: string): Promise<void> {
    await this.makeRequest(`/conversations/${conversationID}/read`, {
      method: 'PUT',
      headers: {
        'X-User-ID': userID,
      },
    });
  }

  async markMessageAsRead(userID: string, messageID: string): Promise<void> {
    await this.makeRequest(`/messages/${messageID}/read`, {
      method: 'PUT',
      headers: {
        'X-User-ID': userID,
      },
    });
  }

  async deleteConversation(userID: string, conversationID: string): Promise<void> {
    await this.makeRequest(`/conversations/${conversationID}`, {
      method: 'DELETE',
      headers: {
        'X-User-ID': userID,
      },
    });
  }

  async search(userID: string, query: string, limit = 50, offset = 0): Promise<SearchResponse> {
    const params = new URLSearchParams({
      q: query,
      limit: limit.toString(),
      offset: offset.toString(),
    });

    return this.makeRequest(`/search?${params}`, {
      headers: {
        'X-User-ID': userID,
      },
    });
  }
}

export const mailboxService = new MailboxService();

